import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useCallback, useContext } from 'react';

import { TokenContext } from '@/Contexts/TokenContext';
import { appInsights } from '@/utils/appInsightsUtil';
import { trackAuthRedirect } from '@/utils/authTelemetry';

import { logError, logWarning } from '@/utils/errorHandlerUtil';
import { stringHasValue } from '@/utils/stringUtil';

// Refresh the access token when it is this close to expiring (or already expired). This matches MSAL's
// default renewal offset (tokenRenewalOffsetSeconds = 300), before which acquireTokenSilent only returns
// the cached token. It only needs to cover a request's time in flight; the 12-hour idle limit doesn't
// matter here because a token that expired while the user was away is refreshed on the next request.
const TOKEN_REFRESH_WINDOW_MS = 5 * 60 * 1000;

type ApiErrorType = 'auth' | 'network' | 'timeout' | 'server' | 'client' | 'parse';

type LoggedError = Error & {
  telemetryLogged?: boolean;
};

export class ApiError extends Error {
  type: ApiErrorType;
  status?: number;
  url?: string;

  constructor(type: ApiErrorType, message: string, options?: { status?: number; url?: string }) {
    super(message);
    this.type = type;
    this.status = options?.status;
    this.url = options?.url;
  }
}

export function useCustomFetch() {
  const { b2cId, authResult, getLatestAuthResult, authPca, updateAccessToken } =
    useContext(TokenContext);

  // Returns the access token to send: the latest one TokenContext has seen, silently refreshed first
  // if it is expired or about to expire. Null means "send the caller's header as-is". Never signs
  // the user out: only an InteractionRequired error (refresh token expired/revoked) sends them back
  // to B2C to sign in again.
  const getCurrentAccessToken = useCallback(
    async (url: string): Promise<string | null> => {
      // authResult stays the same object across silent refreshes (see TokenContext), so read the
      // latest token for its expiry instead.
      const latest = getLatestAuthResult() ?? authResult ?? null;
      const latestToken = stringHasValue(latest?.accessToken) ? latest.accessToken : null;
      const exp: Date | null = latest?.expiresOn ?? null;

      if (exp == null) {
        appInsights.trackTrace({ message: `No expiration date for token of ${b2cId}` });
        return latestToken;
      }

      if (exp.getTime() - Date.now() > TOKEN_REFRESH_WINDOW_MS) {
        return latestToken;
      }

      const account = authPca?.getActiveAccount() ?? latest?.account ?? null;
      if (authPca == null || account == null) {
        logWarning(
          `Access token for ${b2cId} is expiring but there is no MSAL instance to refresh it.`
        );
        return latestToken;
      }

      const scopes = [import.meta.env.VITE_SCOPE as string];

      try {
        const response = await authPca.acquireTokenSilent({ scopes, account });
        updateAccessToken((prevValue) =>
          prevValue?.accessToken === response.accessToken ? prevValue : response
        );
        return response.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          trackAuthRedirect('InteractionRequired', { error, b2cId, trigger: 'tokenRefresh' });
          await authPca.acquireTokenRedirect({ scopes, account });
          throw new ApiError('auth', 'Session expired; signing in again', { url });
        }

        const err = new ApiError('auth', 'Could not refresh access token', { url });
        logError(`Error refreshing access token before fetch to ${url}`, error);
        throw err;
      }
    },
    [authResult, getLatestAuthResult, authPca, b2cId, updateAccessToken]
  );

  const getRequestMethod = useCallback((init?: RequestInit): string => {
    return init?.method?.toUpperCase() ?? 'GET';
  }, []);

  const getDisplayUrl = useCallback((url: string): string => {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch {
      return url;
    }
  }, []);

  const hasAuthorizationHeader = useCallback((headers?: HeadersInit): boolean => {
    if (headers == null) {
      return false;
    }

    if (headers instanceof Headers) {
      return headers.has('Authorization');
    }

    if (Array.isArray(headers)) {
      return headers.some(([key]) => key.toLowerCase() === 'authorization');
    }

    return Object.keys(headers).some((key) => key.toLowerCase() === 'authorization');
  }, []);

  const createFetchTrackingError = useCallback(
    (error: unknown, url: string, init?: RequestInit): LoggedError => {
      const requestMethod = getRequestMethod(init);
      const displayUrl = getDisplayUrl(url);

      const originalError = error instanceof Error ? error : new Error(String(error));
      const originalMessage = stringHasValue(originalError.message)
        ? originalError.message
        : 'Unknown fetch error';
      const isNetworkLevelFailure =
        originalError instanceof TypeError &&
        /failed to fetch|networkerror|load failed/i.test(originalMessage);

      const wrappedError = new Error(
        `API fetch failed: ${requestMethod} ${displayUrl} - ${originalMessage}`
      ) as LoggedError;

      wrappedError.name = isNetworkLevelFailure ? 'ApiUnreachableError' : 'ApiFetchError';
      if (stringHasValue(originalError.stack)) {
        wrappedError.stack = originalError.stack;
      }

      logError(`Error during fetch to ${url}`, wrappedError, {
        fetchMethod: requestMethod,
        fetchUrl: displayUrl,
        originalErrorName: originalError.name,
        originalErrorMessage: originalMessage,
        originalErrorType: typeof error,
        isNetworkLevelFailure,
      });

      wrappedError.telemetryLogged = true;
      return wrappedError;
    },
    [getDisplayUrl, getRequestMethod]
  );

  const handleError = useCallback((error: unknown, url: string) => {
    // TODO(bug): this abort branch is dead code. fetch() rejects with the abort reason (e.g. the
    // string 'Program changed. Data fetching aborted.' from ManagePartners), but
    // customFetchResponse wraps it via createFetchTrackingError (logError + ApiFetchError) before
    // it gets here, so every intentional abort is reported to App Insights as an error. Expected:
    // detect aborts (init.signal?.aborted / AbortError / string reason) in customFetchResponse and
    // rethrow them unwrapped with only a logWarning.
    if (typeof error === 'string' && error.includes('Data fetching aborted')) {
      logWarning(`Fetch to ${url} was aborted`);
      throw new Error(error);
    }

    if (error instanceof Error && (error as LoggedError).telemetryLogged === true) {
      throw error;
    }

    logError(`Error during fetch to ${url}`, error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error(typeof error === 'string' ? error : 'Unknown fetch error');
  }, []);

  const customFetchResponse = useCallback(
    async (url: string, init?: RequestInit): Promise<Response> => {
      if (!hasAuthorizationHeader(init?.headers)) {
        throw new ApiError('auth', 'Authorization header is missing', { url: String(url) });
      }

      // Callers build the Authorization header from the stored (session-stable) authResult, which
      // can lag behind a silent refresh, so always send the current token.
      const accessToken = await getCurrentAccessToken(String(url));
      if (accessToken != null) {
        const headers = new Headers(init?.headers);
        headers.set('Authorization', `Bearer ${accessToken}`);
        init = { ...init, headers };
      }

      appInsights.trackTrace({
        message: `Request to ${url} by ${b2cId} started.`,
      });

      const start = Date.now();

      try {
        const response = await fetch(url, init);

        const end = Date.now();
        const seconds = (end - start) / 1000;
        appInsights.trackTrace({
          message: `Request to ${url} by ${b2cId} completed in ${seconds} seconds. Status: ${response.status}`,
        });

        return response;
      } catch (error: unknown) {
        throw createFetchTrackingError(error, url, init);
      }
    },
    [b2cId, getCurrentAccessToken, createFetchTrackingError, hasAuthorizationHeader]
  );

  const customFetch = useCallback(
    async <T>(url: string, init?: RequestInit): Promise<T> => {
      try {
        const response = await customFetchResponse(url, init);

        const text: string = await response.text().catch(() => '');

        if (!response.ok) {
          // 4xx/5xx – server reachable but unhappy
          let parsedText: unknown = null;
          let errorMsg = `Request failed with status ${response.status}`;

          if (text != null && text !== '') {
            try {
              const parsed: unknown = JSON.parse(text);
              parsedText = parsed;

              // Extract error message from structured response
              if (typeof parsedText === 'object' && parsedText !== null && 'errors' in parsedText) {
                // Handle errors object with validation messages
                const errors = (parsedText as { errors: Record<string, unknown> }).errors;
                const errorList = Object.values(errors).flat();
                errorMsg = Array.isArray(errorList) ? errorList.join(', ') : String(errorList);
              } else if (
                typeof parsedText === 'object' &&
                parsedText !== null &&
                'title' in parsedText
              ) {
                // Use title if available
                errorMsg = String(parsedText.title);
              } else {
                errorMsg = JSON.stringify(parsedText);
              }
            } catch {
              errorMsg = text; // fallback to raw text if not JSON
            }
          }

          const type: ApiErrorType = response.status >= 500 ? 'server' : 'client';

          throw new ApiError(type, errorMsg, {
            status: response.status,
            url: response.url,
          });
        }

        if (text == null || text === '') {
          // Some endpoints might legitimately return nothing – decide what you want here
          return null as T;
        }

        try {
          const returnObj = JSON.parse(text) as T;
          return returnObj;
        } catch {
          return text as unknown as T; // Return raw text if not JSON
        }
      } catch (error: unknown) {
        handleError(error, url);
        return Promise.reject(
          error instanceof Error
            ? error
            : new Error(typeof error === 'string' ? error : 'Unknown fetch error')
        );
      }
    },
    [customFetchResponse, handleError]
  );

  return { customFetch, customFetchResponse };
}
